import { Checkbox } from '#/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#/components/ui/table';
import React from 'react';
import { TablePagination } from './TablePagination';
import { Card } from './ui/card';

type Column<T> = {
  header: string;
  key: keyof T;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  className?: string;
};

type GenericTableProps<T extends { uuid: string | number }> = {
  columns: Column<T>[];
  data: T[];
  selectable?: boolean;
  page?: number;
  itemsPerPages: number;
  totalItems: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  onDoubleClick?: (row: T) => void;
};

export function GenericTable<T extends { uuid: string | number }>({
  columns,
  data,
  selectable = false,
  page = 1,
  itemsPerPages,
  totalItems,
  onPageChange,
  onRowClick,
  onDoubleClick,
}: GenericTableProps<T>) {
  //configure double click handler
  const handleRowDoubleClick = (row: T) => {
    if (onDoubleClick) {
      onDoubleClick(row);
    }
  };

  return (
    <Card className="flex flex-col gap-0 overflow-hidden rounded-[10px] border-none py-0 shadow-none">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[8px] p-0" style={{ maxWidth: '8px' }}>
              &nbsp;
            </TableHead>
            {selectable && (
              <TableHead className="w-[63px] p-2.5">
                <Checkbox className="h-[19px] w-[19px] border border-solid border-[var(--border-color)]" />
              </TableHead>
            )}
            {columns.map((col, idx) => (
              <TableHead
                key={'header' + String(col.key) + idx}
                className={`[font-family:'Poppins',Helvetica] text-sm font-semibold text-[var(--gray-primary)] ${col.className || ''}`}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={'row' + row.uuid}
              // className=" "
              onClick={() => onRowClick?.(row)}
              onDoubleClick={() => handleRowDoubleClick(row)}
            >
              <TableCell className="w-[8px] p-0" style={{ maxWidth: '8px' }}>
                &nbsp;
              </TableCell>
              {selectable && (
                <TableCell className="p-2.5">
                  <Checkbox className="h-[19px] w-[19px] border border-solid border-[var(--border-color)]" />
                </TableCell>
              )}
              {columns.map((col, idx) => {
                const value = row[col.key];
                return (
                  <TableCell key={'value-' + row.uuid + String(col.key) + idx} className={col.className}>
                    {col.render ? col.render(value, row) : (value as React.ReactNode)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {onPageChange && (
        <div className="flex w-full items-center justify-center gap-2 p-4">
          <TablePagination
            page={page}
            itemsPerPage={itemsPerPages}
            totalItems={totalItems}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Card>
  );
}
